import { Box, Paper, Button, TextField } from "@mui/material";
import { styled } from "@mui/material/styles";

export const MainContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "calc(100vh - 72px)", // 48px topbar + 24px MainContent padding
  width: "100%",
  minHeight: "calc(100vh - 72px)",
  backgroundColor: "transparent",
  padding: 0,
  overflow: "hidden",
  justifyContent: "flex-start",
  alignItems: "stretch",
  position: "relative",
  boxSizing: "border-box",
}));

export const FormContainer = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(1),
  padding: "10px",
  width: "100%",
  height: "100%",
  flex: 1,
  minHeight: 0,
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
  border: `1px solid ${theme.palette.divider}`,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  boxSizing: "border-box",
}));

export const HeaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(2),
  flexShrink: 0,
}));

export const FormContent = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: "auto",
  overflowX: "hidden",
  minHeight: 0,
  paddingRight: theme.spacing(1),
  marginRight: theme.spacing(-1),
}));

export const HeaderCircle = styled("div")(({ theme }) => ({
  width: "20px",
  height: "20px",
  borderRadius: "50%",
  backgroundColor: "#3F49A7",
  color: "white",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "11px",
  fontWeight: "600",
  flexShrink: 0,
}));

export const HeaderContent = styled(Box)(({ theme }) => ({
  flex: 1,
}));

export const HeaderTitle = styled("h1")(({ theme }) => ({
  fontSize: "14px",
  fontWeight: "600",
  color: theme.palette.text.primary,
  margin: 0,
  marginBottom: theme.spacing(0.2),
}));

export const HeaderSubtitle = styled("p")(({ theme }) => ({
  fontSize: "11px",
  color: theme.palette.text.secondary,
  margin: 0,
  lineHeight: 1.3,
}));

export const FormSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
}));

export const FormLabel = styled("label")(({ theme }) => ({
  display: "block",
  fontSize: "13px",
  fontWeight: "500",
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(0.8),
  "& .required": {
    color: theme.palette.error.main,
  },
}));

export const StyledTextField = styled(TextField)(({ theme }) => ({
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.spacing(0.5),
    backgroundColor: theme.palette.background.paper,
    fontSize: "14px",
    "& fieldset": {
      borderColor: theme.palette.divider,
    },
    "&:hover fieldset": {
      borderColor: "#3F49A7",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#3F49A7",
      borderWidth: "1px",
    },
  },
  "& .MuiInputBase-input": {
    fontSize: "14px",
    padding: "10px 12px",
  },
}));

export const DescriptionField = styled(TextField)(({ theme }) => ({
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.spacing(0.5),
    backgroundColor: theme.palette.background.paper,
    fontSize: "14px",
    "& fieldset": {
      borderColor: theme.palette.divider,
    },
    "&:hover fieldset": {
      borderColor: "#3F49A7",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#3F49A7",
      borderWidth: "1px",
    },
  },
  "& .MuiInputBase-input": {
    fontSize: "14px",
    padding: "8px 12px",
    minHeight: "20px",
  },
}));

export const FileUploadContainer = styled(Box)(({ theme }) => ({
  border: `1px dashed ${theme.palette.divider}`,
  borderRadius: theme.spacing(1),
  padding: theme.spacing(3),
  textAlign: "center",
  backgroundColor: theme.palette.background.paper,
  cursor: "pointer",
  transition: "all 0.2s ease",
  "&:hover": {
    borderColor: "#3F49A7",
    backgroundColor: theme.palette.action.hover,
  },
}));

export const FileUploadContent = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(1),
}));

export const FileUploadIcon = styled(Box)(({ theme }) => ({
  width: "48px",
  height: "48px",
  borderRadius: "50%",
  backgroundColor: theme.palette.action.hover,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: theme.spacing(1),
  "& svg": {
    fontSize: "24px",
    color: theme.palette.text.secondary,
  },
}));

export const FileUploadText = styled("div")(({ theme }) => ({
  fontSize: "16px",
  fontWeight: "500",
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(0.5),
}));

export const FileUploadSubtext = styled("div")(({ theme }) => ({
  fontSize: "12px",
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
}));

export const FileInfoContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.action.hover,
  borderRadius: theme.spacing(0.5),
  border: `1px solid ${theme.palette.divider}`,
}));

export const TrySampleLink = styled("a")(({ theme }) => ({
  color: "#3F49A7",
  textDecoration: "none",
  fontSize: "13px",
  fontWeight: "500",
  cursor: "pointer",
  "&:hover": {
    textDecoration: "underline",
    color: "#2c357a",
  },
}));

export const ButtonContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(1.5),
  paddingTop: theme.spacing(1.5),
  borderTop: `1px solid ${theme.palette.divider}`,
  flexShrink: 0,
}));

export const CancelButton = styled(Button)(({ theme }) => ({
  padding: "4px 10px",
  fontSize: "12px",
  fontWeight: "500",
  textTransform: "none",
  borderRadius: theme.spacing(0.5),
  minHeight: "28px",
  minWidth: "70px",
  color: theme.palette.text.secondary,
  backgroundColor: "transparent",
  border: `1px solid ${theme.palette.divider}`,
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.text.secondary,
  },
}));

export const NextButton = styled(Button)(({ theme }) => ({
  padding: "4px 12px",
  fontSize: "12px",
  fontWeight: "600",
  textTransform: "none",
  borderRadius: theme.spacing(0.5),
  minHeight: "28px",
  minWidth: "70px",
  background: "linear-gradient(135deg, #3F49A7 0%, #2c357a 100%)",
  color: "white",
  boxShadow: "0 2px 8px rgba(63,73,167,0.25)",
  transition: "all 0.2s ease",
  "&:hover": {
    background: "linear-gradient(135deg, #2c357a 0%, #1e2a4a 100%)",
    boxShadow: "0 4px 12px rgba(63,73,167,0.35)",
    transform: "translateY(-1px)",
  },
  "&:disabled": {
    background: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
    boxShadow: "none",
    transform: "none",
  },
}));

export const DropdownContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  "& .MuiSelect-root": {
    borderRadius: theme.spacing(0.5),
    backgroundColor: theme.palette.background.paper,
    fontSize: "14px",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.divider,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
      borderWidth: "1px",
    },
  },
}));

export const PreviewContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(1),
  overflow: "hidden",
  backgroundColor: theme.palette.background.paper,
}));

export const PreviewHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.grey[50],
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

export const PreviewTitle = styled("h3")(({ theme }) => ({
  margin: 0,
  fontSize: "14px",
  fontWeight: "600",
  color: theme.palette.text.primary,
}));

export const PreviewTable = styled("table")(({ theme }) => ({
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "12px",
}));

export const PreviewTableHead = styled("thead")(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
}));

export const PreviewTableRow = styled("tr")(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: theme.palette.grey[25],
  },
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

export const PreviewTableHeader = styled("th")(({ theme }) => ({
  padding: theme.spacing(1),
  textAlign: "left",
  borderBottom: `2px solid ${theme.palette.divider}`,
  borderRight: `1px solid ${theme.palette.divider}`,
  fontWeight: "600",
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.grey[100],
  position: "relative",
  fontSize: "12px",
  "&:last-child": {
    borderRight: "none",
  },
}));

export const PreviewTableCell = styled("td")(({ theme }) => ({
  padding: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderRight: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.primary,
  fontSize: "12px",
  "&:last-child": {
    borderRight: "none",
  },
}));

export const EditableHeaderInput = styled("input")(({ theme }) => ({
  border: "none",
  background: "transparent",
  fontSize: "12px",
  fontWeight: "600",
  color: theme.palette.text.primary,
  width: "100%",
  padding: "2px 4px",
  borderRadius: "2px",
  "&:focus": {
    outline: `2px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.background.paper,
  },
}));

export const TableHeaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "4px",
}));
